import { forwardRef, useContext, useEffect, useImperativeHandle, useRef, useState, } from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import {
    balanceAtom,
    cardsAtom,
    lastGenerateUUIDAtom,
    modalObjAtom,
    prohibitAtom,
    regenePrivateSetAtom,
    showRegenerateAtom,
    showSubscribeAtom,
    subscriptionsInfoAtom,
    taskDetailAtom,
    userPermissionAtom,
} from '../../../store';
import { getTaskDetail, purchaseLicense, setPrivate, } from '../../../utils/net';
import beforelicenseIcon from '../../../assets/beforeLisenceicon.png';
import beforelicensebg from '../../../assets/beforeLicense.png';
import afterlicensebg from '../../../assets/afterlicense.png';
import afterlicenseIcon from '../../../assets/afterLisenceicon.png';
import style from './license.module.css';
import { useTips } from '../../../common/GlobalTips';
import { useRefreshInfo } from '../../../utils/hooks';
import { trackEvent } from '../../../utils/trackEvent'
import * as Sentry from "@sentry/react";
import { useTranslation } from "react-i18next";
import { PermissionContext } from '../../../utils/context';

const License = forwardRef((props, ref) => {
    const {
        style: myStyle,
        largest,
        isLisenced,
        uuid,
        cancel = true,
        immediate = true,
    } = props;
    const [isLicenseHovered, setIsLicenseHovered] = useState(false);
    const setRegenePrivate = useSetRecoilState(regenePrivateSetAtom);
    const lastGenerateUUID = useRecoilValue(lastGenerateUUIDAtom);
    const [isLicense, setIsLicense] = useState(isLisenced);
    const prohibit = useRecoilValue(prohibitAtom);
    const [cards, setCards] = useRecoilState(cardsAtom);
    const [userPermission, setUserPermission] = useRecoilState(userPermissionAtom);
    const [isPay, setIsPay] = useState(false);
    const setShowRegenerate = useSetRecoilState(showRegenerateAtom);
    // const logInfo = useRecoilValue(logInfoAtom);
    const balance = useRecoilValue(balanceAtom);
    const popupRef = useRef();
    const tip = useTips();
    const { refreshInfo, userData } = useRefreshInfo()
    const taskDetail = useRecoilValue(taskDetailAtom);
    const subscriptionsInfo = useRecoilValue(subscriptionsInfoAtom);
    const setShowSubscribe = useSetRecoilState(showSubscribeAtom)
    const setModalObj = useSetRecoilState(modalObjAtom)
    const { t } = useTranslation();
    const { isBusinessOrAbove } = useContext(PermissionContext);
    /*
        1. generate: 应该先license再private
        2. detail：如果是license，设置private不用管，如果是已经private，此时设置license需要执行private
      */

    const handleHoverLicenseEnter = () => {
        if ((isLicense && !cancel) || prohibit) return;
        setIsLicenseHovered(true);
    };

    const handleHoverLicenseLeave = () => {
        setIsLicenseHovered(false);
    };

    const handlePopupMouseLeave = () => {
        setIsLicenseHovered(false);
    };

    const handlerLicense = async () => {
        if (prohibit) return;
        if (isLicense) {
            setIsLicenseHovered(false);
            return;
        }
        try {
            trackEvent('ChatAvatar - License')
            const data = await purchaseLicense({ task_uuid: uuid });
            if (!data.data.error) {
                setIsLicense(true);
                if (lastGenerateUUID === uuid) {
                    setShowRegenerate(false);
                    setRegenePrivate({
                        oldPrivate: '',
                        currentPrivate: '',
                        privatedChange: '',
                    });
                    // setLastGenerateUUID(false)
                }
                // 如果is_private，需要退回其private次数
                const response = await getTaskDetail(uuid);
                refreshInfo()
                if (response.data.is_private) {
                    const res = await setPrivate(uuid);
                    if (!res.data.error) {

                        setUserPermission((pre) => {
                            return {
                                ...pre,
                                privateQuantity: userData?.meta?.chances_left_to_set_private_task
                            }
                        })
                    }
                }
            } else {
                tip({ type: 'error', presetText: 'INSUFFICIENT_BALANCE' });
            }
            setIsLicenseHovered(false);
        } catch (e) {
            Sentry.captureException(e)
        }
    };

    const handlerLicenseDelay = async () => {
        setIsLicenseHovered(false);
        if (isLicense) {
            setIsLicenseHovered(false);
            return;
        }

        if ((balance.selfBalance + subscriptionsInfo.wallet_balance) < 400) {
            tip({ type: 'error', presetText: 'INSUFFICIENT_BALANCE' });
            return;
        }
        try {
            setIsLicense(true);
            setIsPay(true);
            setIsLicenseHovered(false);
            refreshInfo('all')
        } catch (e) {
            Sentry.captureException(e)
        }
    };

    const handlerLicenseCanler = () => {
        // 取消接口
        setIsPay(false);
        setIsLicense(false);
        setIsLicenseHovered(false);
    };

    const changeCards = () => {
        const newCards = JSON.parse(JSON.stringify(cards));
        const index = newCards.findIndex((item) => item.task_uuid === uuid);
        if (index === -1) return;
        newCards[index].lisenced = isLicense;
        setCards(newCards);
    };

    useImperativeHandle(ref, () => ({
        pay: async (task_uuid) => {
            if (!isPay) return;
            const data = await purchaseLicense({ task_uuid });
            if (!data.data.error) {
                setIsLicense(true);
                setShowRegenerate(false);
                setRegenePrivate({
                    oldPrivate: '',
                    currentPrivate: '',
                    privatedChange: '',
                });
                // setLastGenerateUUID(false)
                refreshInfo('all')
            }
        },
    }));

    useEffect(() => {
        if (isLicense) changeCards();

    }, [isLicense]);

    const openSubscribe = () => {
        setModalObj((pre) => {
            return {
                ...pre,
                state: false
            }
        })
        setShowSubscribe(true);
    }

    return (
        <div
            className={`${style.licenseBox} ${largest ? style.largest : ''}`}
            style={{
                ...myStyle,
                cursor: prohibit ? 'not-allowed' : '',
                backgroundImage: `url(${isLicense ? afterlicensebg : beforelicensebg})`,
            }}
            onMouseEnter={handleHoverLicenseEnter}
            onMouseLeave={handleHoverLicenseLeave}>
            <img src={isLicense ? afterlicenseIcon : beforelicenseIcon} alt="" />
            <span>License</span>
            <div
                className={style.hoverWrapper}
                style={{ display: isLicenseHovered ? 'block' : 'none' }}
                onMouseLeave={handlePopupMouseLeave}
                ref={popupRef}>
                <br />
                <div
                    style={{
                        width: '324px',
                        backgroundColor: '#fff',
                    }}
                />
                <div className={style.hoverBox}>
                    <div
                        className={style.licenseTitle}
                        style={{
                            display: 'flex',
                            justifyContent: 'flex-start',
                            alignItems: 'center',
                        }}
                    >
                        <span className={style.licenseTip}>License</span>
                        <span className={style.price}>{isBusinessOrAbove() && (taskDetail ? taskDetail.author.user_uuid
                            === localStorage.getItem('user_uuid') : true) ? 0 : 5}&nbsp;</span>
                        <span>/&nbsp;asset</span>
                    </div>
                    <p
                        style={{
                            marginBottom: (isLicense && cancel) ? '8px' : '16px',
                        }}
                    >
                        {t('LICENSE_TITLE_1')}
                        {' '}
                        <a
                            style={{ color: '#5D1BE3' }}
                            href="https://creativecommons.org/licenses/by-sa/4.0/"
                            target="_blank"
                            rel="noreferrer"
                        >
                            {t('LICENSE_TITLE_2')}
                        </a>
                        .
                    </p>
                    <div
                        onPointerDown={handlerLicenseCanler}
                        style={{
                            display: (isLicense && cancel) ? 'block' : 'none',
                        }}
                        className={style.licenseCanler}
                    >
                        Not available yet
                    </div>
                    <div className={style.btns}>
                        <div
                            onPointerDown={immediate ? handlerLicense : handlerLicenseDelay}
                            className={`${style.licenseButton} ${style.confirmButton}`}>
                            Confirm
                        </div>
                        {!isBusinessOrAbove() && <div className={`${style.licenseButton} ${style.freeButton}`} onClick={openSubscribe}>
                            Get for free
                        </div>}
                    </div>
                </div>
            </div>
        </div>
    );
});

License.displayName = "License"
export default License;
